/**
 * Where a marketing email's footer link lands. It unsubscribes only when the customer presses the
 * button, because mail scanners fetch every link with GET and a page that unsubscribed on its own
 * would opt them out without asking (MSG-042). The button POSTs the signed token. An unknown or
 * cut-off token is a dead end with a way to reach support.
 */
import { useEffect, useState } from "react";
import { Button } from "@lattice/ui/Button";
import { LinkButton } from "@lattice/ui/LinkButton";
import { Status } from "@lattice/ui/Status";
import { unsubscribe } from "./api.ts";
import { AuthLayout } from "./AuthLayout.tsx";
import { SUPPORT_ADDRESS } from "./PublicPage.tsx";
import { usePath } from "./router.ts";

const PREFIX = "/unsubscribe/";

function tokenFrom(path: string): string | null {
  if (!path.startsWith(PREFIX)) return null;
  const rest = path.slice(PREFIX.length);

  return rest === "" || rest.includes("/") ? null : rest;
}

const COPY = {
  ask: {
    heading: "Stop these emails",
    body: "Select Unsubscribe to stop marketing emails to this address. Formula, order and check-in emails keep coming.",
  },
  done: {
    heading: "You've unsubscribed",
    body: "Marketing emails to this address will stop. You will still hear about your formula, orders and check-ins.",
  },
  offline: {
    heading: "We could not reach Lattice",
    body: "Check your connection and try again. Nothing was changed.",
  },
  failed: {
    heading: "This link did not work",
    body: `This link has expired or was cut off. Write to ${SUPPORT_ADDRESS} from the address that got the email and we will stop them.`,
  },
} as const;

type Phase = "ask" | "pending" | "done" | "failed" | "offline";

function wording(phase: Phase): (typeof COPY)[keyof typeof COPY] {
  if (phase === "done") return COPY.done;

  if (phase === "failed") return COPY.failed;

  if (phase === "offline") return COPY.offline;

  return COPY.ask;
}

function Action({ phase, onConfirm }: { readonly phase: Phase; readonly onConfirm: () => void }) {
  if (phase === "done")
    return (
      <>
        <Status state="ok">Nothing more to do. You can close this page.</Status>
        <LinkButton href="/" tone="quiet">
          Back to Lattice
        </LinkButton>
      </>
    );

  if (phase === "failed")
    return <LinkButton href={`mailto:${SUPPORT_ADDRESS}`}>Email support</LinkButton>;

  return (
    <Button testId="confirm-unsubscribe" wide loading={phase === "pending"} onClick={onConfirm}>
      {phase === "offline" ? "Try again" : "Unsubscribe"}
    </Button>
  );
}

export function Unsubscribe() {
  const token = tokenFrom(usePath());
  const [phase, setPhase] = useState<Phase>(token === null ? "failed" : "ask");
  const { heading, body } = wording(phase);

  useEffect(() => {
    document.title = "Unsubscribe · Lattice";
  }, []);

  const confirm = () => {
    if (token === null || (phase !== "ask" && phase !== "offline")) return;
    setPhase("pending");
    unsubscribe(token)
      .then((ok) => setPhase(ok ? "done" : "failed"))
      .catch(() => setPhase("offline"));
  };

  return (
    <AuthLayout
      title={heading}
      lead={body}
      focusHeading={phase === "done" || phase === "failed" || phase === "offline"}
    >
      <Action phase={phase} onConfirm={confirm} />
    </AuthLayout>
  );
}
